import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import {
  VideoCall,
  People,
  OpenInNew,
  Download,
  Schedule,
  CalendarToday,
  CheckCircle,
  Cancel,
  PlayArrow,
  Stop,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'
import {
  useGetCourtroomByRoundQuery,
  useJoinCourtroomMutation,
  useEndCourtroomMutation,
  useSetRecordingMutation,
  CourtroomStatus,
  type Courtroom,
} from '../store/api/courtroomsApi'
import { useGetCaseRoundsQuery, RoundType } from '../store/api/roundsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { formatCaseDisplay } from '../utils/caseDisplay'

const STATUS_LABELS: Record<CourtroomStatus, string> = {
  [CourtroomStatus.SCHEDULED]: 'Đã lên lịch',
  [CourtroomStatus.ACTIVE]: 'Đang diễn ra',
  [CourtroomStatus.COMPLETED]: 'Đã kết thúc',
  [CourtroomStatus.CANCELLED]: 'Đã hủy',
}

const STATUS_COLORS: Record<CourtroomStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [CourtroomStatus.SCHEDULED]: 'info',
  [CourtroomStatus.ACTIVE]: 'success',
  [CourtroomStatus.COMPLETED]: 'default',
  [CourtroomStatus.CANCELLED]: 'error',
}

export default function CourtroomPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: cases = [] } = useGetCasesQuery()
  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  const [selectedRoundId, setSelectedRoundId] = useState<string>('')
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false)
  const [recordingUrl, setRecordingUrl] = useState('')
  const [selectedCourtroomId, setSelectedCourtroomId] = useState<string>('')

  const canManageCourtrooms = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')

  // Get rounds for selected case
  const { data: rounds = [] } = useGetCaseRoundsQuery(selectedCaseId, {
    skip: !selectedCaseId,
  })

  // Filter ONLINE_HEARING rounds only
  const onlineHearingRounds = rounds.filter(r => r.round_type === RoundType.ONLINE_HEARING)

  // Auto-select first case when cases are loaded
  useEffect(() => {
    if (cases.length > 0 && !selectedCaseId) {
      setSelectedCaseId(cases[0].id)
    }
  }, [cases, selectedCaseId])

  // Auto-select first round when rounds are loaded
  useEffect(() => {
    if (onlineHearingRounds.length > 0 && selectedCaseId && !selectedRoundId) {
      setSelectedRoundId(onlineHearingRounds[0].id)
    }
  }, [onlineHearingRounds, selectedCaseId, selectedRoundId])

  // Get courtroom for selected round
  const { data: courtroom, isLoading: isLoadingCourtroom, refetch: refetchCourtroom } = useGetCourtroomByRoundQuery(
    selectedRoundId,
    {
      skip: !selectedRoundId,
    }
  )

  const [joinCourtroom, { isLoading: isJoining }] = useJoinCourtroomMutation()
  const [endCourtroom, { isLoading: isEnding }] = useEndCourtroomMutation()
  const [setRecording, { isLoading: isSettingRecording }] = useSetRecordingMutation()

  const handleJoinCourtroom = async () => {
    if (!courtroom) return
    try {
      const result = await joinCourtroom(courtroom.id).unwrap()
      if (result.meeting_link) {
        window.open(result.meeting_link, '_blank')
      }
      refetchCourtroom()
    } catch (error: any) {
      alert(`Tham gia courtroom thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleEndCourtroom = async () => {
    if (!courtroom) return
    if (!confirm('Bạn có chắc muốn kết thúc courtroom này?')) return
    try {
      await endCourtroom(courtroom.id).unwrap()
      refetchCourtroom()
      alert('Courtroom đã kết thúc!')
    } catch (error: any) {
      alert(`Kết thúc courtroom thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleOpenRecordingDialog = (courtroomId: string) => {
    setSelectedCourtroomId(courtroomId)
    setRecordingUrl('')
    setRecordingDialogOpen(true)
  }

  const handleSetRecording = async () => {
    if (!recordingUrl.trim()) {
      alert('Vui lòng nhập recording URL')
      return
    }
    try {
      await setRecording({ courtroomId: selectedCourtroomId, recordingUrl: recordingUrl.trim() }).unwrap()
      setRecordingDialogOpen(false)
      refetchCourtroom()
      alert('Recording URL đã được cập nhật!')
    } catch (error: any) {
      alert(`Cập nhật recording thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const selectedCase = cases.find(c => c.id === selectedCaseId)

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <VideoCall sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Virtual Courtroom
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        p: { xs: 2, sm: 3 }, 
        mb: { xs: 2, sm: 3 }, 
        borderRadius: { xs: 1, sm: 2 } 
      }}>
        <Typography variant="h6" gutterBottom sx={{ 
          mb: { xs: 2, sm: 3 }, 
          fontWeight: 600,
          fontSize: { xs: '1.1rem', sm: '1.25rem' }
        }}>
          Chọn cuộc thi và vòng thi
        </Typography>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="case-select-label">Cuộc thi</InputLabel>
              <Select
                labelId="case-select-label"
                value={selectedCaseId}
                label="Cuộc thi"
                onChange={(e) => {
                  setSelectedCaseId(e.target.value)
                  setSelectedRoundId('')
                }}
                sx={{
                  textTransform: 'none',
                  '& .MuiSelect-select': {
                    py: { xs: 1.25, sm: 1.5 },
                  },
                }}
              >
                <MenuItem value="">
                  <em>-- Chọn cuộc thi --</em>
                </MenuItem>
                {cases.map((caseItem) => (
                  <MenuItem key={caseItem.id} value={caseItem.id}>
                    {formatCaseDisplay(caseItem)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!selectedCaseId || onlineHearingRounds.length === 0}>
              <InputLabel id="round-select-label">Vòng tranh tụng online</InputLabel>
              <Select
                labelId="round-select-label"
                value={selectedRoundId}
                label="Vòng tranh tụng online"
                onChange={(e) => setSelectedRoundId(e.target.value)}
                sx={{
                  textTransform: 'none',
                  '& .MuiSelect-select': {
                    py: { xs: 1.25, sm: 1.5 },
                  },
                }}
              >
                <MenuItem value="">
                  <em>-- Chọn vòng thi --</em>
                </MenuItem>
                {onlineHearingRounds.map((round) => (
                  <MenuItem key={round.id} value={round.id}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {round.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(round.start_date).toLocaleDateString('vi-VN')} - {new Date(round.end_date).toLocaleDateString('vi-VN')}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedCaseId && onlineHearingRounds.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Cuộc thi này chưa có vòng tranh tụng online.
              </Alert>
            )}
          </Grid>
        </Grid>
      </Paper>

      {selectedRoundId && (
        <Box>
          {isLoadingCourtroom ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : !courtroom ? (
            <Alert severity="info">
              Courtroom chưa được tạo cho vòng thi này. Courtroom sẽ được tạo tự động khi chuyển sang vòng ONLINE_HEARING.
            </Alert>
          ) : (
            <Card elevation={3} sx={{ borderRadius: { xs: 1, sm: 2 } }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'start' }, 
                  mb: { xs: 2, sm: 3 },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1.5, sm: 0 }
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {selectedCase?.title || 'Courtroom'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                      Vòng: {onlineHearingRounds.find(r => r.id === selectedRoundId)?.name || 'N/A'}
                    </Typography>
                  </Box>
                  <Chip
                    label={STATUS_LABELS[courtroom.status]}
                    color={STATUS_COLORS[courtroom.status]}
                    size="medium"
                    sx={{ 
                      fontWeight: 'bold', 
                      mt: { xs: 0.5, sm: 0 },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  />
                </Box>

                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Meeting Link
                      </Typography>
                      {courtroom.meeting_link ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: { xs: 'flex-start', sm: 'center' }, 
                          gap: 1,
                          flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              wordBreak: 'break-all',
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              flex: 1
                            }}
                          >
                            {courtroom.meeting_link}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => window.open(courtroom.meeting_link, '_blank')}
                            color="primary"
                            sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                          >
                            <OpenInNew />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                          Chưa có meeting link
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Thời gian
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2">
                          {courtroom.started_at
                            ? new Date(courtroom.started_at).toLocaleString('vi-VN')
                            : 'Chưa bắt đầu'}
                        </Typography>
                      </Box>
                      {courtroom.ended_at && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Stop fontSize="small" color="action" />
                          <Typography variant="body2">
                            Kết thúc: {new Date(courtroom.ended_at).toLocaleString('vi-VN')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Participants
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <People fontSize="small" color="action" />
                        <Typography variant="body1" fontWeight={600}>
                          {courtroom.participants?.length || 0} người tham gia
                        </Typography>
                      </Box>
                      {courtroom.attendance && Object.keys(courtroom.attendance).length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Thời gian tham gia được ghi nhận
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {courtroom.recording_url && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Recording
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<Download />}
                          href={courtroom.recording_url}
                          target="_blank"
                          variant="outlined"
                        >
                          Tải Recording
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>

              <CardActions sx={{ 
                p: { xs: 1.5, sm: 2 }, 
                pt: 0, 
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 0 },
                alignItems: { xs: 'stretch', sm: 'center' }
              }}>
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  {courtroom.meeting_link && (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<OpenInNew />}
                      onClick={handleJoinCourtroom}
                      disabled={isJoining || courtroom.status === CourtroomStatus.COMPLETED || courtroom.status === CourtroomStatus.CANCELLED}
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                        },
                        textTransform: 'none',
                        fontWeight: 500,
                        width: { xs: '100%', sm: 'auto' },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      {isJoining ? <CircularProgress size={20} /> : 'Tham gia Google Meet'}
                    </Button>
                  )}
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  {canManageCourtrooms && courtroom.status === CourtroomStatus.ACTIVE && (
                    <>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Stop />}
                        onClick={handleEndCourtroom}
                        disabled={isEnding}
                        size="medium"
                        sx={{ 
                          textTransform: 'none', 
                          fontWeight: 500,
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        {isEnding ? <CircularProgress size={20} /> : 'Kết thúc Courtroom'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => handleOpenRecordingDialog(courtroom.id)}
                        size="medium"
                        sx={{ 
                          textTransform: 'none', 
                          fontWeight: 500,
                          width: { xs: '100%', sm: 'auto' }
                        }}
                      >
                        Thêm Recording
                      </Button>
                    </>
                  )}
                </Box>
              </CardActions>
            </Card>
          )}
        </Box>
      )}

      {/* Recording Dialog */}
      <Dialog 
        open={recordingDialogOpen} 
        onClose={() => setRecordingDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 1, sm: 2 },
            borderRadius: { xs: 1, sm: 2 },
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Thêm Recording URL
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            label="Recording URL"
            value={recordingUrl}
            onChange={(e) => setRecordingUrl(e.target.value)}
            placeholder="https://drive.google.com/..."
            sx={{ mt: { xs: 1, sm: 2 } }}
            helperText="Nhập URL của recording từ Google Drive hoặc nguồn khác"
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button 
            onClick={() => setRecordingDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSetRecording}
            variant="contained"
            disabled={isSettingRecording || !recordingUrl.trim()}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {isSettingRecording ? <CircularProgress size={20} /> : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
